import { useEffect, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import fetch from '../../../utils/fetch';
import {
  getFundraisingCostsExplanation,
  getGiftAidAmount,
  getPreferencesForView,
  normalisePreferencesValues,
} from '../../../utils/donationForm';
import { buildGtmCallback, pushPurchase } from '../../../utils/gtm';
import { sessionStorageGetItem, sessionStorageSetItem } from '../../../utils/web-storage';
import { DEFAULT_COUNTRY_CODE } from '../../../utils/constants';

type QueryParams = {
  orderId: string;
  paymentId: string;
  intentId: string;
};

const CharityDonationSendGTMPurchase = ({ isThankYouPage }: { isThankYouPage: boolean }) => {
  const searchParams = useSearchParams();
  const [needSendGTMPurchase, setNeedSendGTMPurchase] = useState(false);
  const [queryParams, setQueryParams] = useState<QueryParams>();

  useLayoutEffect(() => {
    if (
      isThankYouPage &&
      searchParams.get('cw_send_gtm_purchase_event') &&
      searchParams.get('orderID') &&
      searchParams.get('redirect_url') &&
      searchParams.get('payment_id') &&
      searchParams.get('intent_id') &&
      searchParams.get('mode') === 'modal' &&
      sessionStorageGetItem('gtm_purchase_sent') != searchParams.get('orderID')
    ) {
      setNeedSendGTMPurchase(true);
      setQueryParams({
        orderId: searchParams.get('orderID') as string,
        paymentId: searchParams.get('payment_id') as string,
        intentId: searchParams.get('intent_id') as string,
      });
    }
  }, []);

  // Send GTM Purchase event for Charity donation modal ONLY.
  useEffect(() => {
    const handleSuccessReturn = async (orderId: string, paymentId: string, intentId: string) => {
      if (orderId) {
        // Making a secure request to the backend to obtain order details.
        const responseMetadata = await fetch(`/commerce/order/${orderId}/metadata`, {
          query: { payment_id: paymentId, intent_id: intentId },
        });
        if (responseMetadata.body?.data) {
          // Contact preferences from backend don't take into account default
          // preference value, and it leads to wrong results in normalisePreferencesValues()
          // called in pushSubmittedDataToGtm().
          let contactPreferences = responseMetadata.body.data.contact_preferences;
          const alignedContactPreferences = [] as string[];
          const viewedPreferences = getPreferencesForView(false, false) as {
            value: string;
            valueBackend: string;
          }[];
          viewedPreferences.forEach(({ value, valueBackend }) => {
            if (
              (contactPreferences.includes(value) && valueBackend !== 'denied') ||
              (contactPreferences.includes(value) &&
                DEFAULT_COUNTRY_CODE === 'GB' &&
                value === 'post' &&
                valueBackend === 'denied')
            ) {
              alignedContactPreferences.push(value);
            }
          });

          contactPreferences = normalisePreferencesValues(
            alignedContactPreferences,
            viewedPreferences,
          );

          // Create a clean data object with all necessary transformations
          const processedData = {
            ...responseMetadata.body.data,
            contact_preferences: contactPreferences,
          };

          const isCompanyDonation =
            processedData.company_donation &&
            processedData.company_donation[0] === 'company_donation';

          const products = [
            {
              name: processedData.appeal_name,
              id: processedData.appeal_id,
              price: processedData.totalAmount,
              category:
                processedData.type === 'charity_recurring_donation'
                  ? 'Modal monthly donation'
                  : 'Modal single donation',
              variant:
                processedData.type === 'charity_recurring_donation'
                  ? 'recurring_donation'
                  : 'single_donation',
              quantity: 1,
              contact_preferences: contactPreferences,
              source_code: processedData.source_code || '',
              fundraising_costs_applied: processedData.fundraisingCostsApplied,
              fundraising_costs_amount: processedData.fundraisingCostsAmount,
              fundraising_costs_formula: getFundraisingCostsExplanation(
                processedData.fundraisingCostsFormula,
              ),
              gift_aid: processedData.giftAid,
              gift_aid_amount: getGiftAidAmount(processedData.totalAmount, processedData.giftAid),
              fundraising_costs_element_type: 'dropdown',
              company_donation: isCompanyDonation,
            },
          ];
          const eventCallback = () => {
            sessionStorageSetItem('gtm_purchase_sent', orderId);
            window.history.replaceState({}, 'Thank you page', searchParams.get('redirect_url'));
          };
          pushPurchase(
            { id: orderId, revenue: processedData.totalAmount, option: 'Modal' },
            products,
            buildGtmCallback(eventCallback),
          );
        }
      }
    };

    if (needSendGTMPurchase && queryParams) {
      handleSuccessReturn(queryParams.orderId, queryParams.paymentId, queryParams.intentId);
    }
  }, [needSendGTMPurchase]);

  return null;
};

export default CharityDonationSendGTMPurchase;
